/**
 * Ccontainerskeleton Component
 * Loading skeleton with compositor opacity and scale animation
 */
'use client';
import type { CSSProperties } from 'react';
import { useState, useEffect } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Ccontainer } from '../Ccontainer';
import { ContainerPreset } from '@/constants/componentPresets';
import { dataEl, type DataElScope } from '@/utils/dataEl';
// CSS keyframe pulse (globals.css) — runs on the compositor. A framer-motion
// keyframe loop froze inside CproductContentVersion's subtree (static skeleton).
const SKELETON_PULSE_ANIMATION_NAME = 'cabal-skeleton-pulse';
const DEFAULT_RANGE_ANIMATION_MS = 3000;
export interface CcontainerskeletonProps {
    /** The component rendering this skeleton, e.g. "Cusermenu/UsageCardSkeleton". Required: it is
     *  what makes an inspected node say WHICH surface it belongs to. */
    dataElScope: DataElScope;
    height?: number | string;
    width?: number | string;
    heightRange?: {
        min: number;
        max: number;
        animateEvery?: number | true;
    };
    widthRange?: {
        min: number;
        max: number;
        animateEvery?: number | true;
    };
    radius?: number;
    className?: string;
    style?: CSSProperties;
}
const getDeterministicRangeValue = (range: {
    min: number;
    max: number;
}): number => {
    return Math.floor((range.min + range.max) / 2);
};
export function Ccontainerskeleton({ dataElScope, height = 120, width = '100%', heightRange, widthRange, radius, className, style, }: CcontainerskeletonProps) {
    const shouldReduceMotion = useReducedMotion() === true;
    const [randomHeight, setRandomHeight] = useState<number | string>(heightRange ? heightRange.min : height);
    const [randomWidth, setRandomWidth] = useState<number | string>(widthRange ? widthRange.min : width);
    const [dimensionsInitialized, setDimensionsInitialized] = useState(false);
    useEffect(() => {
        if (dimensionsInitialized) {
            return;
        }
        const frameId = requestAnimationFrame(() => {
            if (heightRange) {
                setRandomHeight(getDeterministicRangeValue(heightRange));
            }
            if (widthRange) {
                setRandomWidth(getDeterministicRangeValue(widthRange));
            }
            setDimensionsInitialized(true);
        });
        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [dimensionsInitialized, heightRange, widthRange]);
    useEffect(() => {
        if (heightRange?.animateEvery) {
            const intervalMs = heightRange.animateEvery === true
                ? DEFAULT_RANGE_ANIMATION_MS
                : heightRange.animateEvery;
            const interval = setInterval(() => {
                setRandomHeight(getDeterministicRangeValue(heightRange));
            }, intervalMs);
            return () => clearInterval(interval);
        }
    }, [heightRange]);
    useEffect(() => {
        if (widthRange?.animateEvery) {
            const intervalMs = widthRange.animateEvery === true
                ? DEFAULT_RANGE_ANIMATION_MS
                : widthRange.animateEvery;
            const interval = setInterval(() => {
                setRandomWidth(getDeterministicRangeValue(widthRange));
            }, intervalMs);
            return () => clearInterval(interval);
        }
    }, [widthRange]);
    const pulseDuration = 2.25;
    return (
    <div data-el={dataEl(dataElScope, 'Root')} style={{
            width: '100%',
            height: '100%',
            ...(shouldReduceMotion
                ? {}
                : { animation: `${SKELETON_PULSE_ANIMATION_NAME} ${pulseDuration}s ease-in-out infinite` }),
        }}>
      <Ccontainer preset={ContainerPreset.ModalCard} height={randomHeight} width={randomWidth} radius={radius} className={className} style={style}/>
    </div>
    );
}
